import { Injectable } from '@angular/core';

import { AnalyticsBase } from '@core/analytics-legacy';
import { AnalyticsProps } from '@core/analytics-types';
import type { DateRangeValueType } from '@core/date-range-types';
import {
  ReportDashboardComponent,
  ReportLibraryComponentCategory,
  ReportDashboardComponentOrigin,
  ReportDashboardComponentViewMode,
  ReportDashboardComponentKind,
} from '@core/reports-types';
import { formatTrackedFilters } from '@core/reports-util';
import type { VisibilityLevel } from '@core/visibility-types';

type TrackedComponent<T = ReportDashboardComponentKind> = Record<
  string,
  string | number | string[]
> & {
  kind: T;
  viewMode: ReportDashboardComponentViewMode;
  metric: string;
  dateFilter: string;
  paramFilter: string[];
  groups: string[];
  interval: string;
  datePoints?: [string, string];
  generated: boolean;
} & (T extends ReportDashboardComponentKind.TimeBased
    ? {
        datePoints: [string, string];
      }
    : {});

type TrackedDetailsCommonProps = AnalyticsProps<{
  metric: string;
  resourceType: string;
}>;

type TrackedDetailsColumnProps = AnalyticsProps<{
  columnId: string;
  columnPosition: number;
  columnVisible: boolean;
}>;

type TrackedDashboard = AnalyticsProps<{
  templateId: string | null;
  origin: 'duplicate' | 'template' | 'blank';
  generated: boolean;
}>;

const trackedComponentFormat = (
  component: ReportDashboardComponent,
): AnalyticsProps<TrackedComponent> => {
  const { dateFilter, paramFilter } = formatTrackedFilters(component.dateFilter, component.filters);
  return {
    kind: component.kind!,
    viewMode: component.viewMode!,
    metric: component.metric!,
    dateFilter: dateFilter!,
    paramFilter: paramFilter!,
    groups: <string[]>[component.primaryGroup, component.secondaryGroup].filter(g => !!g),
    templateId: component.templateId,
    interval: component.settings?.interval!,
    generated: component.generated!,
    ...(component.kind === ReportDashboardComponentKind.TimeBased && {
      datePoints: [component.startPoint!, component.endPoint!],
    }),
  };
};

@Injectable({ providedIn: 'root' })
export class ReportsAnalytics extends AnalyticsBase {
  public readonly namespace = 'Reports';
  public readonly requireViewContext = false;

  public dashboardAction(
    action: 'duplicate' | 'delete' | 'exportPng' | 'exportCsv' | 'exportXlsx' | 'exportPdf',
    data: TrackedDashboard & {
      visibility: VisibilityLevel;
    },
  ): void {
    this.track('DashboardAction', {
      action,
      ...data,
    });
  }

  public dashboardCreated(
    data: Omit<TrackedDashboard, 'generated'> & {
      visibility: VisibilityLevel;
    },
  ): void {
    this.track('DashboardCreated', data);
  }

  public dashboardDeleted(
    data: TrackedDashboard & {
      visibility: VisibilityLevel;
    },
  ): void {
    this.track('DashboardDeleted', data);
  }

  public dashboardFilterSetDate(
    params: TrackedDashboard & {
      id: number | string;
      dateRange: number;
      dateRangeType: DateRangeValueType;
    },
  ) {
    this.track('DashboardFilterSetDate', {
      ...params,
    });
  }

  public dashboardFilterAddGroup(
    params: TrackedDashboard & { id: number | string; group: string },
  ) {
    this.track('DashboardFilterAddGroup', {
      ...params,
    });
  }

  public dashboardFilterRemoveGroup(
    params: TrackedDashboard & { id: number | string; group: string },
  ) {
    this.track('DashboardFilterRemoveGroup', {
      ...params,
    });
  }

  public dashboardFilterReset(params: TrackedDashboard & { id: number | string }) {
    this.track('DashboardFilterReset', {
      ...params,
    });
  }

  public dashboardLayoutChanged(params: TrackedDashboard & { id: number | string }) {
    this.track('DashboardLayoutChanged', {
      ...params,
    });
  }

  public dashboardComponentCreated(
    component: ReportDashboardComponent,
    origin: ReportDashboardComponentOrigin,
  ): void {
    this.track('ComponentCreated', {
      ...trackedComponentFormat(component),
      origin,
    });
  }

  public dashboardComponentUpdated(
    component: ReportDashboardComponent,
    origin: ReportDashboardComponentOrigin,
  ): void {
    this.track('ComponentUpdated', {
      ...trackedComponentFormat(component),
      origin,
    });
  }

  public dashboardComponentDeleted(
    component: ReportDashboardComponent,
    origin: ReportDashboardComponentOrigin,
  ): void {
    this.track('ComponentDeleted', {
      ...trackedComponentFormat(component),
      origin,
    });
  }

  public libraryFiltersChanged(query: string, sidebarFilter: ReportLibraryComponentCategory): void {
    this.track('ComponentLibraryFiltersChanged', {
      query,
      sidebarFilter,
    });
  }

  public componentBuilderOpen(): void {
    this.track('ComponentBuilderOpen');
  }

  public componentBuilderAbandoned(): void {
    this.track('ComponentBuilderAbandoned');
  }

  public componentBuilderControlChange(props: {
    field: string;
    fieldValue: any;
    editing: boolean;
    activeMetricKind: string;
    activeMetricName: string;
    datePoints?: [string | null, string | null];
  }): void {
    this.track('ComponentBuilderControlChanged', props);
  }

  public componentTemplateAdded(count: number, templateIds: string[]): void {
    this.track('ComponentTemplateAdded', { count, templateIds });
  }

  public componentLibraryOpened(): void {
    this.track('ComponentLibraryOpened');
  }

  public componentLibraryClosed(): void {
    this.track('ComponentLibraryClosed');
  }

  public dashboardViewed(
    params: TrackedDashboard & {
      visibility: VisibilityLevel;
      name: string;
      id: number;
      componentsTemplateIds: string[];
      componentsMetrics: string[];
    },
  ): void {
    this.track('DashboardViewed', params);
  }

  public startFromScratchOpen() {
    this.track('StartFromScratchOpened');
  }

  public startFromScratchKindSelected(kind: ReportDashboardComponentKind) {
    this.track('StartFromScratchKindSelected', { kind });
  }

  public startFromScratchMetricSelected(metric: string) {
    this.track('StartFromScratchMetricSelected', { metric });
  }

  public startFromScratchAbandoned() {
    this.track('StartFromScratchAbandoned');
  }

  public detailsViewed(
    resourceType: string,
    metric: string,
    visualizationType: ReportDashboardComponentViewMode,
    primaryGroup?: string | null,
    secondaryGroup?: string | null,
  ): void {
    this.track('DetailsViewed', {
      resourceType,
      metric,
      visualizationType,
      ...(primaryGroup ? { primaryGroup } : {}),
      ...(secondaryGroup ? { secondaryGroup } : {}),
    });
  }

  public componentAction(
    action:
      | 'edit'
      | 'fullscreen'
      | 'duplicate'
      | 'delete'
      | 'move'
      | 'exportCsv'
      | 'exportXlsx'
      | 'exportPng'
      | 'exportPdf',
    component: Pick<
      TrackedComponent,
      'metric' | 'visualizationType' | 'templateId' | 'origin' | 'generated'
    >,
  ) {
    this.track('ComponentAction', {
      action,
      ...component,
    });
  }

  public detailsTableOptionsDropdownOpen(event: TrackedDetailsCommonProps) {
    this.track('DetailsTableOptionsDropdownOpened', event);
  }

  public detailsTableOptionsAutoFitChange(event: TrackedDetailsCommonProps & { autoFit: boolean }) {
    this.track('DetailsTableOptionsAutoFitChanged', event);
  }

  public detailsTableColumnPositionChange(
    event: TrackedDetailsCommonProps & TrackedDetailsColumnProps,
  ) {
    this.track('DetailsTableColumnPositionChanged', event);
  }

  public detailsTableColumnVisibilityChange(
    event: TrackedDetailsCommonProps & TrackedDetailsColumnProps,
  ) {
    this.track('DetailsTableColumnVisibilityChanged', event);
  }

  public shareFeedbackLink() {
    this.track('SidebarFeedbackLinkClicked');
  }

  public dashboardDataPreviewToggled(templateId: string, enabled: boolean) {
    this.track('DashboardDataPreviewToggled', {
      templateId,
      enabled,
    });
  }

  public dashboardTemplateSelected(templateId: string, realDataPreview: boolean) {
    this.track('DashboardTemplateSelected', {
      templateId,
      realDataPreview,
    });
  }

  public dashboardLibraryOpened() {
    this.track('DashboardLibraryOpened');
  }

  public dashboardLibraryClosed() {
    this.track('DashboardLibraryClosed');
  }

  public dashboardLibraryFiltersChanged(query: string) {
    this.track('DashboardLibraryFiltersChanged', { query });
  }
}
